import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  signal,
} from '@angular/core';

@Component({
  selector: 'app-colored-scale',
  imports: [CommonModule],
  templateUrl: './colored-scale.component.html',
  styleUrls: ['./colored-scale.component.scss'],
})
export class ColoredScaleComponent implements OnChanges {
  @Input() title: string = 'Escala';
  @Input() min: number = -1;
  @Input() max: number = 1;
  @Input() ticks: number = 5;
  @Input() colorMin: string = '#FA1C1C'; // rojo
  @Input() colorMid: string = '#F9EE55'; // amarillo
  @Input() colorMax: string = '#4AB769'; // verde

  // NUEVO: rampa de colores completa
  @Input() colorRamp?: string[];

  // NUEVO: indica si la escala debe mostrarse en escala de grises con baja opacidad
  @Input() disabled: boolean = false;

  isExpanded = signal(true);

  tickValues = signal<number[]>([]);

  ngOnChanges(changes: SimpleChanges): void {
    this.generateTicks();
  }

  toggleExpanded(): void {
    this.isExpanded.update(v => !v);
  }

  generateTicks(): void {
    const rangeMin = this.min;
    const rangeMax = this.max;

    const step = (rangeMax - rangeMin) / (this.ticks - 1);
    this.tickValues.set(
      Array.from({ length: this.ticks }, (_, i) =>
        parseFloat((rangeMin + i * step).toFixed(2))
      )
        .slice()
        .reverse()
    );
  }

  /**
   * Devuelve el gradiente CSS para la barra de color
   * Si hay colorRamp, usa todos los colores de la rampa equidistantemente
   * Si no, usa el modo antiguo de 3 colores
   * Si disabled es true, convierte los colores a escala de grises
   */
  getColorGradient(): string {
    let gradient: string;

    if (this.colorRamp && this.colorRamp.length > 1) {
      // Usar toda la rampa de colores
      const stops = this.colorRamp.map((color, i) => {
        const percent = (i / (this.colorRamp.length - 1)) * 100;
        const finalColor = this.disabled
          ? this.convertToGrayscale(color)
          : color;
        return `${finalColor} ${percent.toFixed(1)}%`;
      });
      gradient = `linear-gradient(to top, ${stops.join(', ')})`;
    } else {
      // Modo antiguo (fallback)
      const colorMin = this.disabled
        ? this.convertToGrayscale(this.colorMin)
        : this.colorMin;
      const colorMid = this.disabled
        ? this.convertToGrayscale(this.colorMid)
        : this.colorMid;
      const colorMax = this.disabled
        ? this.convertToGrayscale(this.colorMax)
        : this.colorMax;
      gradient = `linear-gradient(to top, ${colorMin} 0%, ${colorMid} 50%, ${colorMax} 100%)`;
    }

    return gradient;
  }

  /**
   * Convierte un color hexadecimal a escala de grises
   */
  private convertToGrayscale(color: string): string {
    // Remover el # si está presente
    const hex = color.replace('#', '');

    // Convertir a RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calcular el valor de escala de grises usando la fórmula de luminancia
    const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);

    // Convertir de vuelta a hexadecimal
    const grayHex = gray.toString(16).padStart(2, '0');

    return `#${grayHex}${grayHex}${grayHex}`;
  }

  /**
   * Obtiene el valor mínimo a mostrar (zoom o valor original)
   */
  getDisplayMin(): number {
    return this.min;
  }

  /**
   * Obtiene el valor máximo a mostrar (zoom o valor original)
   */
  getDisplayMax(): number {
    return this.max;
  }

  /**
   * Obtiene el valor medio a mostrar
   */
  getDisplayMid(): number {
    const displayMin = this.getDisplayMin();
    const displayMax = this.getDisplayMax();
    return (displayMin + displayMax) / 2;
  }
}
